import React, { useLayoutEffect } from 'react';
import { useActionData, useAsyncValue, useSubmit } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import debug from 'debug';

// Logger
const logPage = debug('rois:newcommunication:page');

// Components Styled
import { Container } from './styles';

// Components
import NewCommunicationForm from '../../../../components/forms/NewCommunicationForm';

// Utils
import { createFormDataFromValues } from '../../../../utils/forms';

// Types
import { NewCommunication } from '../../../../types/communication';
import { formNaming } from '../../../../components/forms/NewCommunicationForm/constants';

// Store
import { tabsType } from '../../../../state/reducers/ui/tabs';
import { tabs } from '../../../../state/actions/ui/tabs';
import { roidOTSelected } from '../../../../state/actions/ui/roidOTSelectedGeneralView';
import { OrderType } from '../../../../types/orders';

// Types
interface ActionDataNewCommunication {
  parentOrderId: string;
  parentOrderRoid: string;
}

export default function NewCommunicationPage(): JSX.Element {
  // Habilitar logs en desarrollo
  if (import.meta.env.DEV) {
    debug.enable('rois:newcommunication:*');
  }

  const loaderData = useAsyncValue() as NewCommunication;
  const actionData = useActionData() as ActionDataNewCommunication | undefined;
  const submit = useSubmit();
  const dispatch = useDispatch();

  // Log inicial del componente
  logPage('🔵 [NewCommunicationPage] RENDER');
  logPage('  - loaderData.parentOrderId:', (loaderData as any)?.parentOrderId);
  logPage('  - actionData?.parentOrderId:', actionData?.parentOrderId);

  // Dispatch roidOTSelected if coming from a work order
  // Tras recargar no hay `actionData` (la puerta): la miga sale del padre de
  // la copia de ESTA pestaña (el loader solo la da si es de la OT de la URL).
  // `roidOTSelected` se persiste en `localStorage`, compartido entre pestañas:
  // sin esto la miga enseñaba la OT de la última puerta de OTRA pestaña
  // (revisión de la Task 12a).
  useLayoutEffect(() => {
    const parentFromLoader = loaderData as
      | (NewCommunication & { parentOrderId?: string; parentOrderRoid?: string })
      | undefined;
    const parentOrderId = actionData?.parentOrderId ?? parentFromLoader?.parentOrderId;
    const parentOrderRoid = actionData?.parentOrderRoid ?? parentFromLoader?.parentOrderRoid;
    if (parentOrderId && parentOrderRoid) {
      dispatch(roidOTSelected(OrderType.Communication, parentOrderRoid, parentOrderId));
    }
  }, [actionData, loaderData, dispatch]);

  // FIX BUG #35 (T60/T68): Preseleccionar tab "Relacionar con O.T." cuando viene de ShowOT
  useLayoutEffect(() => {
    logPage('🟡 [NewCommunicationPage] useLayoutEffect para tabs EJECUTÁNDOSE');

    const hasParentOrder =
      actionData?.parentOrderId ||
      (loaderData as NewCommunication & { parentOrderId?: string })
        ?.parentOrderId;

    if (hasParentOrder) {
      const parentRoid = actionData?.parentOrderRoid ||
        (loaderData as NewCommunication & { parentOrderRoid?: string })?.parentOrderRoid;
      const isCampaignParent = parentRoid?.startsWith('Ca');

      logPage('✅ [NewCommunicationPage] DESPACHANDO tabs(COMMUNICATION, %d)', isCampaignParent ? 2 : 1);
      dispatch(tabs(tabsType.COMMUNICATION, isCampaignParent ? 2 : 1));
    }
  }, [actionData, loaderData, dispatch]);

  const onFormSubmit = async (values: NewCommunication) => {
    const formData = createFormDataFromValues(values);
    submit(formData, {
      method: 'POST',
      action: 'summary',
    });
  };

  return (
    <Container>
      <NewCommunicationForm
        initialData={loaderData}
        onFormSubmit={onFormSubmit}
        namingPersistForm={formNaming.NEW_COMMUNICATION_FORM}
        namingTabsType={tabsType.COMMUNICATION}
      />
    </Container>
  );
}
